// ============================================================================
// BULK MUTATION HOOKS (Apollo GraphQL) FOR TIME ENTRIES
// ============================================================================

import { useMemo } from "react";
import { useMutation } from "@apollo/client/react";
import {
  DeleteTimeEntriesDocument,
  DeleteTimeEntriesMutation,
  UpdateTimeEntriesDocument,
  UpdateTimeEntriesMutation,
} from "@/src/gql/graphql";
import { nowIso } from "./graphql-meta";

type BulkHookResult = {
  mutate: (ids: string[]) => Promise<Record<string, unknown>>;
  isLoading: boolean;
  error: unknown;
  isError: boolean;
  isSuccess: boolean;
};

function buildBulkResult(
  result: { loading: boolean; error: unknown },
  mutateFn: BulkHookResult["mutate"]
): BulkHookResult {
  return {
    mutate: mutateFn,
    isLoading: result.loading,
    error: result.error,
    isError: !!result.error,
    isSuccess: !result.loading && !result.error,
  };
}

export function useBulkApproveTimeEntries(): BulkHookResult {
  const [mutate, result] = useMutation<UpdateTimeEntriesMutation>(UpdateTimeEntriesDocument);

  const mutateFn = useMemo(
    () => async (ids: string[]) => {
      const results = await Promise.all(
        ids.map((id) =>
          mutate({
            variables: {
              id,
              input: {
                is_approved: true,
                approved_at: nowIso(),
                updated_at: nowIso(),
              },
            },
            errorPolicy: "all",
          })
        )
      );

      const errors = results.filter((r) => r.error);
      if (errors.length > 0) {
        throw new Error(`Failed to approve ${errors.length} time entries`);
      }

      return { success: true, approvedCount: ids.length };
    },
    [mutate]
  );

  return buildBulkResult(result, mutateFn);
}

export function useBulkRejectTimeEntries(): BulkHookResult {
  const [mutate, result] = useMutation<UpdateTimeEntriesMutation>(UpdateTimeEntriesDocument);

  const mutateFn = useMemo(
    () => async (ids: string[]) => {
      const results = await Promise.all(
        ids.map((id) =>
          mutate({
            variables: {
              id,
              input: {
                is_approved: false,
                approved_by: null,
                approved_at: null,
                updated_at: nowIso(),
              },
            },
            errorPolicy: "all",
          })
        )
      );

      const errors = results.filter((r) => r.error);
      if (errors.length > 0) {
        throw new Error(`Failed to reject ${errors.length} time entries`);
      }

      return { success: true, rejectedCount: ids.length };
    },
    [mutate]
  );

  return buildBulkResult(result, mutateFn);
}

export function useBulkDeleteTimeEntries(): BulkHookResult {
  const [mutate, result] = useMutation<DeleteTimeEntriesMutation>(DeleteTimeEntriesDocument);

  const mutateFn = useMemo(
    () => async (ids: string[]) => {
      const results = await Promise.all(
        ids.map((id) =>
          mutate({
            variables: { id },
            errorPolicy: "all",
          })
        )
      );

      const errors = results.filter((r) => r.error);
      if (errors.length > 0) {
        throw new Error(`Failed to delete ${errors.length} time entries`);
      }

      return { success: true, deletedCount: ids.length };
    },
    [mutate]
  );

  return buildBulkResult(result, mutateFn);
}
